<template>
  <div class="base-modal" @keydown.esc="closeModal">
    <div
      ref="modal"
      v-on-click-outside="closeOnClickOutsideHandler"
      class="base-modal__modal"
      :class="modalClass"
      :aria-label="modalName"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
    >
      <div class="base-modal__wrapper" :class="wrapperClass">
        <h2 v-if="title" class="base-modal__title font-l">
          {{ title }}
        </h2>
        <div class="base-modal__content" :class="contentClass">
          <slot></slot>
          <div v-if="showCloseButton" class="base-modal__close">
            <base-button :label="closeModalLabel" type="button" @click="closeModal"></base-button>
          </div>
        </div>
        <button
          v-if="showXButton"
          ref="closeXBtn"
          class="base-modal__x-btn"
          :aria-label="closeModalLabel"
          type="button"
          @click="closeModal"
        >
          <i class="icon icon-Close" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, watchEffect, onBeforeUnmount, useTemplateRef, provide } from 'vue';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import { useI18n } from 'vue-i18n';
import BaseButton from '../../2_elements/BaseButton/BaseButton.vue';
import { useAppStore } from '../../../stores/appStore';
import { vOnClickOutside } from '@vueuse/components';
import useId from '../../../composables/useId';
import type { BaseModalProps, ModalContext } from './types';

const { t } = useI18n();
const appStore = useAppStore();
const backdropId = `BaseModal-${useId()}`;

const emit = defineEmits<{
  closeModal: [];
}>();

const props = withDefaults(defineProps<BaseModalProps>(), {
  isVisible: false,
  modalName: 'Modal',
  showCloseButton: false,
  showXButton: true,
  closeOnClickOutside: false,
  modalClass: '',
  wrapperClass: '',
  contentClass: '',
});

const modal = useTemplateRef('modal');
const closeXBtn = useTemplateRef('closeXBtn');

let savedModalScrollTop = 0;

const blockModalScroll = (): void => {
  const el = modal.value;
  if (!el) return;

  savedModalScrollTop = el.scrollTop;
  el.style.setProperty('--modal-scroll-offset', `${savedModalScrollTop}px`);
  el.style.overflow = 'hidden';
};

const unblockModalScroll = (): void => {
  const el = modal.value;

  el.style.overflow = '';
  el.style.removeProperty('--modal-scroll-offset');
  el.scrollTo({ top: savedModalScrollTop, left: 0, behavior: 'instant' });
};

const initialFocusElement = computed(() => {
  if (props.showXButton && closeXBtn.value) {
    return closeXBtn.value;
  }
  return modal.value;
});

const { deactivate } = useFocusTrap(modal, {
  allowOutsideClick: true,
  immediate: true,
  preventScroll: true,
  initialFocus: () => initialFocusElement.value,
});

const closeModalLabel = computed(() => {
  return props.closeBtnText ? props.closeBtnText : t('close');
});

const closeModal = () => {
  emit('closeModal');
};

const closeOnClickOutsideHandler = computed(() =>
  props.closeOnClickOutside ? closeModal : () => null
);

watchEffect(() => {
  appStore.setBackdrop(backdropId, { enable: props.isVisible, global: true });
});

onBeforeUnmount(() => {
  savedModalScrollTop = 0;
  deactivate();
  appStore.unblockScrolling();
  appStore.setBackdrop(backdropId, { enable: false });
});

onMounted(() => {
  appStore.blockScrolling();
});

provide<ModalContext>('modalContext', {
  element: modal,
  blockModalScroll,
  unblockModalScroll,
});
</script>

<style lang="scss">
@import './scss/BaseModal.scss';
</style>
